import axios from "axios";
import Cookies from "js-cookie";
import { announceSessionEnded } from "../lib/sessionActivity";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "http://localhost:4000",
  headers: { "Content-Type": "application/json" }
});

api.interceptors.request.use((config) => {
  // Prefer localStorage (persists across app restarts in the Capacitor
  // WebView), fall back to the cookie backup.
  let token: string | null = null;
  try {
    token = localStorage.getItem("igrowth_token");
  } catch {
    /* storage disabled */
  }
  if (!token) token = Cookies.get("igrowth_token") || null;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

/**
 * One narrow response interceptor, for one status.
 *
 * The app deliberately has no global error handling — each screen shows its own
 * failures. But a 402 from the club-trial gate is not that screen's failure:
 * the request was refused for a reason that has nothing to do with what the
 * person typed, and every form would otherwise have to learn the same sentence.
 * Throttled, because one blocked save can fan out into several requests.
 */
let lastTrialToastAt = 0;

/**
 * What a 401 has to say for us to treat it as "your session is over".
 *
 * Not every 401 means that. Changing your password answers 401 when the
 * current one is wrong; the login endpoint answers 401 for a bad password,
 * before there is a session at all. Being signed out for a typo would be
 * absurd, and a list of URLs to exclude would drift out of step with the
 * server the first time somebody added a route.
 *
 * So the server marks the ones that really are about the token (see
 * SESSION_INVALID in backend middleware/auth.ts) and we act on nothing else.
 */
const SESSION_INVALID = "session_invalid";

api.interceptors.response.use(
  (r) => r,
  (error) => {
    const data = error?.response?.data;
    const status = error?.response?.status;

    /**
     * A refused token ends the session, once.
     *
     * The app has no global error handling by design — each screen shows its
     * own failures. But a 401 is not a screen's failure: nothing the person
     * typed caused it and nothing they do on that screen can fix it. Without
     * this, an expired or revoked token left somebody apparently signed in
     * while every page quietly rendered empty, which is the worst of both
     * states. The auth context listens for the announcement and signs out.
     */
    if (status === 401 && data?.code === SESSION_INVALID) {
      announceSessionEnded("expired");
    }

    if (status === 402 && data?.code === "CLUB_TRIAL_EXPIRED") {
      const now = Date.now();
      if (now - lastTrialToastAt > 8000) {
        lastTrialToastAt = now;
        // Imported lazily: this file is pulled in by scripts and tests that
        // have no DOM to render a toast into.
        import("react-hot-toast")
          .then((m) => m.default.error(data.error || "Clubul este în pauză.", { duration: 6000 }))
          .catch(() => {});
      }
    }
    return Promise.reject(error);
  }
);

// Generic API request helper
export async function apiRequest<T = any>(
  url: string,
  options?: {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    data?: any;
    params?: any;
  }
): Promise<T> {
  const response = await api.request<T>({
    url,
    method: options?.method || 'GET',
    data: options?.data,
    params: options?.params,
  });
  return response.data;
}

export type MeResponse = {
  user: {
    id: string;
    email: string;
    full_name: string | null;
    profile_url?: string | null;
    phone?: string | null;
    date_of_birth?: string | null;
    locale: string;
    created_at: string;
    avatar_url?: string | null; // legacy, use profile_url
    status?: "active" | "demo" | "inactive";
    demo_started_at?: string | null;
    demo_expires_at?: string | null;
  };
  roles: { id: string; code: string; scope_type: "platform"|"club"|"team"; scope_id: string|null }[];
  clubs: {
    id: string;
    name: string;
    logo_url: string | null;
    plan_code: string;
    trial_ends_at?: string | null;
    capabilities?: import("./clubs").ClubCapabilities;
    access?: import("./clubs").ClubAccess;
  }[];
  activeRole: "platform_admin"|"club_admin"|"coach"|"athlete" | string;
  athleteProfile?: {
    guardian_contact: { name?: string; email?: string; phone?: string; relationship?: string } | null;
    has_guardian: boolean;
  } | null;
  demoLimits?: {
    isDemo: boolean;
    limits: { MAX_CLUBS: number; MAX_COACHES: number; MAX_ATHLETES: number };
    current: { clubs: number; coaches: number; athletes: number };
  } | null;
  // Populated only when the active token came from POST /auth/impersonate.
  // The AuthContext uses it to render the "logged in as X" banner.
  impersonator?: { id: string; email: string; full_name: string | null } | null;
};

export async function getMe() {
  const { data } = await api.get<MeResponse>("/auth/me");
  return data;
}

/**
 * Platform-admin only: ask the backend to mint a 1-hour token for the
 * given user. The caller is responsible for stashing the original
 * token + swapping cookies (AuthContext.startImpersonation does that).
 */
export async function impersonateUser(userId: string): Promise<{
  token: string;
  user: { id: string; email: string; full_name: string | null };
  impersonator: { id: string; email: string; full_name: string | null } | null;
  expires_in_seconds: number;
}> {
  const { data } = await api.post(`/auth/impersonate/${userId}`);
  return data;
}

export async function getClubs() {
  // acum backendul întoarce [{ club, role }]
  const { data } = await api.get<{
    club: {
      id: string;
      name: string;
      logo_url: string | null;
      description: string | null;
      address: string | null;
      plan_code: string;
      created_at: string;
    },
    role: string
  }[]>("/clubs");
  return data;
}

export type AgeGroupRef = {
  id: string;
  name: string;
  min_age: number | null;
  max_age: number | null;
};

export type QuizDifficulty = "easy" | "medium" | "hard";

export type AgeGroup = AgeGroupRef & {
  display_order: number;
  is_active: boolean;
  default_difficulty?: QuizDifficulty | null;
  created_at?: string;
  updated_at?: string;
};

export async function getAgeGroups(opts?: { all?: boolean }): Promise<AgeGroup[]> {
  const qs = opts?.all ? "?all=true" : "";
  const { data } = await api.get<AgeGroup[]>(`/age-groups${qs}`);
  return data;
}

export async function createAgeGroup(payload: { name: string; min_age?: number | null; max_age?: number | null; display_order?: number; is_active?: boolean; default_difficulty?: QuizDifficulty | null }): Promise<AgeGroup> {
  const { data } = await api.post<AgeGroup>(`/age-groups`, payload);
  return data;
}

export async function updateAgeGroup(id: string, payload: Partial<{ name: string; min_age: number | null; max_age: number | null; display_order: number; is_active: boolean; default_difficulty: QuizDifficulty | null }>): Promise<AgeGroup> {
  const { data } = await api.patch<AgeGroup>(`/age-groups/${id}`, payload);
  return data;
}

export async function deleteAgeGroup(id: string): Promise<{ success: true }> {
  const { data } = await api.delete<{ success: true }>(`/age-groups/${id}`);
  return data;
}

export async function getClubTeams(clubId: string) {
  const { data } = await api.get<{ id: string; name: string; age_group?: string|null; age_group_id?: string|null; age_group_ref?: AgeGroupRef|null; min_age?: number|null; sport_type?: string|null; coach_id?: string|null }[]>(`/clubs/${clubId}/teams`);
  return data;
}

export interface CoachTeam {
  id: string;
  name: string;
  sport_type: string | null;
  age_group: string | null;
  age_group_id?: string | null;
  age_group_ref?: AgeGroupRef | null;
  min_age?: number | null;
  club: { id: string; name: string };
  created_at: string;
  athletes: {
    id: string;
    email: string;
    full_name: string;
    profile_url: string | null;
    phone: string | null;
    date_of_birth: string | null;
    gender: string | null;
    joined_team_at: string;
    vulnerability_flag: boolean;
  }[];
  athlete_count: number;
}

export async function getCoachTeams(userId: string): Promise<CoachTeam[]> {
  const { data } = await api.get<{ teams: CoachTeam[] }>(`/users/${userId}/coach-teams`);
  return data.teams || [];
}

export type ClubSettings = {
  sports: string[];
  age_groups: string[];
  /** Opt-in per-sport coordinators. Off for most clubs. */
  coordinators_enabled?: boolean;
  /**
   * Whether payment reminders also reach the athlete. On by default; a club
   * that says no still has its parents reminded in full.
   */
  reminders_notify_athlete?: boolean;
  /** Percent off a sibling's fee. 0 means the club gives no sibling discount. */
  sibling_discount_percent?: number;
  /** `additional` = everyone but the first child enrolled; `all` = everyone. */
  sibling_discount_mode?: "additional" | "all";
};

export async function getClubSettings(clubId: string): Promise<ClubSettings> {
  const { data } = await api.get<ClubSettings>(`/clubs/${clubId}/settings`);
  return data;
}

export async function updateClubSettings(clubId: string, payload: Partial<ClubSettings>): Promise<ClubSettings> {
  const { data } = await api.patch<ClubSettings>(`/clubs/${clubId}/settings`, payload);
  return data;
}

export type ClubSport = { id: string; name: string };

/**
 * The club's sports, already resolved to catalogue entries.
 *
 * `clubs.sports` can hold a mix of ids and legacy names; the server resolves
 * them so callers never have to know that.
 */
export async function getClubSports(clubId: string): Promise<ClubSport[]> {
  const { data } = await api.get<{ sports: ClubSport[] }>(`/clubs/${clubId}/sports`);
  return data.sports || [];
}

export type ClubScopeInfo = {
  is_club_admin: boolean;
  is_coordinator: boolean;
  is_coach: boolean;
  /** null = every sport; [] = none assigned yet, so nothing is visible. */
  sport_ids: string[] | null;
  sports: ClubSport[];
};

/** What the signed-in user may see in this club. */
export async function getClubScope(clubId: string): Promise<ClubScopeInfo> {
  const { data } = await api.get<ClubScopeInfo>(`/clubs/${clubId}/scope`);
  return data;
}

export async function createTeam(clubId: string, payload: { name: string; age_group?: string; age_group_id?: string | null; sport_id?: string; sport_type?: string; coach_id?: string; min_age?: number | null }) {
  const { data } = await api.post(`/clubs/${clubId}/teams`, payload);
  return data;
}

export async function updateTeam(teamId: string, payload: { name?: string; age_group?: string; age_group_id?: string | null; sport_id?: string | null; sport_type?: string; coach_id?: string | null; min_age?: number | null }) {
  const { data } = await api.patch(`/teams/${teamId}`, payload);
  return data;
}

export async function deleteTeam(teamId: string) {
  const { data } = await api.delete(`/teams/${teamId}`);
  return data;
}

/**
 * The team's whole coaching staff, replaced in one call.
 *
 * `coach_ids` is the complete list: anyone left out is taken off this team.
 * Their account, their club membership and their other teams are untouched —
 * removing somebody from a team is not removing them from the club.
 *
 * `head_coach_id` must be one of `coach_ids`; omit it and the first of the
 * list becomes head coach.
 */
export async function setTeamCoaches(
  teamId: string,
  coachIds: string[],
  headCoachId?: string | null
): Promise<{ ok: true; coach_ids: string[]; head_coach_id: string | null }> {
  const { data } = await api.put(`/teams/${teamId}/coaches`, {
    coach_ids: coachIds,
    ...(headCoachId === undefined ? {} : { head_coach_id: headCoachId }),
  });
  return data;
}

export type Athlete = { id: string; email: string; full_name: string | null; profile_url?: string | null };

export async function getTeamAthletes(
  teamId: string,
  opts?: { q?: string; page?: number; pageSize?: number }
) {
  const params = new URLSearchParams();
  if (opts?.q) params.append("q", opts.q);
  if (opts?.page) params.append("page", String(opts.page));
  if (opts?.pageSize) params.append("pageSize", String(opts.pageSize));

  const qs = params.toString();
  const { data } = await api.get(`/teams/${teamId}/athletes${qs ? `?${qs}` : ""}`);

  // data are forma: { total, page, pageSize, data: Athlete[] }
  return data as { total: number; page: number; pageSize: number; data: Athlete[] };
}

// export async function getTeamAthletesArray(teamId: string) {
//   const res = await getTeamAthletes(teamId);
//   return res.data; // doar lista
// }

export async function createSession(payload: {
  team_id: string; type: "sport" | "educational"; title: string; start_at: string; end_at: string;
  location_text?: string; location_id?: string;
  // Admin attribution: when a club_admin creates the session, they can
  // assign it to a specific coach instead of becoming the coach themselves.
  coach_id?: string;
}) {
  const { data } = await api.post(`/sessions`, payload);
  return data;
}

/**
 * Create a recurring series of sessions. weekdays are 0=Sun..6=Sat (matching
 * JS Date.getDay), weeks is how many weeks the series spans (1-52).
 * Returns { sessions: [...], count: N }.
 */
export async function createSessionSeries(payload: {
  team_id: string; type: "sport" | "educational"; title: string; start_at: string; end_at: string;
  location_text?: string; location_id?: string;
  coach_id?: string;
  weekdays: number[];
  weeks: number;
}) {
  const { data } = await api.post<{ sessions: any[]; count: number }>(`/sessions/series`, payload);
  return data;
}

export async function getTeamSessions(teamId: string) {
  const { data } = await api.get(`/sessions/team/${teamId}`);
  return data as any[];
}

/**
 * One team's sessions inside a window — what a month calendar needs.
 *
 * `from`/`to` are ISO instants and filter on start_at, so a session that
 * starts in the window is in it. The backend scopes by role on top of this,
 * so a coach asking about somebody else's team gets nothing back.
 */
export async function getTeamSessionsInRange(
  teamId: string,
  from: string,
  to: string
): Promise<ClubSession[]> {
  const { data } = await api.get<{ data: ClubSession[] }>(`/sessions`, {
    params: { team_id: teamId, from, to, pageSize: 500 },
  });
  return data.data || [];
}

export async function setAttendance(sessionId: string, records: { user_id: string; status: "present" | "late" | "absent" | "excused"; notes?: string; reason?: string }[]) {
  const { data } = await api.post(`/sessions/${sessionId}/attendance`, { records });
  return data;
}

// Session attachments
export interface SessionAttachment {
  id: string;
  session_id: string;
  file_url: string;
  file_name: string;
  file_size: number;
  file_type: string;
  uploaded_by: string;
  created_at: string;
  uploader?: { id: string; first_name: string | null; last_name: string | null };
}

export async function getSessionAttachments(sessionId: string): Promise<SessionAttachment[]> {
  // The endpoint returns a bare array; this used to read `data.attachments`,
  // so the list came back empty no matter what had been uploaded. Both shapes
  // are accepted now so the call survives either contract.
  const { data } = await api.get<SessionAttachment[] | { attachments: SessionAttachment[] }>(
    `/sessions/${sessionId}/attachments`
  );
  if (Array.isArray(data)) return data;
  return data?.attachments ?? [];
}

export async function uploadSessionAttachment(sessionId: string, file: File): Promise<SessionAttachment> {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post<SessionAttachment>(`/sessions/${sessionId}/attachments`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function deleteSessionAttachment(sessionId: string, attachmentId: string): Promise<void> {
  await api.delete(`/sessions/${sessionId}/attachments/${attachmentId}`);
}

export async function updateSession(
  sessionId: string,
  payload: Partial<{
    title: string;
    type: "sport" | "educational";
    start_at: string;
    end_at: string;
    coach_id: string;
    location_id: string | null;
    location_text: string | null;
    notes: string | null;
  }>
) {
  const { data } = await api.patch(`/sessions/${sessionId}`, payload);
  return data;
}

export async function deleteSession(sessionId: string): Promise<void> {
  await api.delete(`/sessions/${sessionId}`);
}

/** Which occurrences of a recurring training an edit applies to. */
export type SeriesScope = "following" | "all";

export type SeriesEditResult = {
  ok: true;
  scope: SeriesScope;
  /** Occurrences touched at all (title, coach, place). */
  updated: number;
  /** Of those, how many were actually moved to a new hour. */
  retimed: number;
  /** Already held, so left at their own hour on purpose. */
  skipped_past: number;
};

/**
 * Change a whole recurring series, not just one occurrence.
 *
 * `start_at` / `end_at` are the EDITED occurrence's new times; the server
 * works out how far it moved and shifts the others by the same amount, each
 * keeping its own date. Trainings that already happened keep their hour — the
 * attendance taken at them was taken then — and a clash with another booking
 * refuses the whole edit rather than moving half a term.
 */
export async function updateSessionSeries(
  sessionId: string,
  payload: Partial<{
    title: string;
    type: "sport" | "educational";
    start_at: string;
    end_at: string;
    coach_id: string;
    location_id: string | null;
    location_text: string | null;
    notes: string | null;
  }> & { scope: SeriesScope }
): Promise<SeriesEditResult> {
  const { data } = await api.patch<SeriesEditResult>(`/sessions/${sessionId}/series`, payload);
  return data;
}

/**
 * Cancel a recurring training. "following" drops this occurrence and the ones
 * after it; occurrences that already happened are kept whatever the scope,
 * because they carry the register.
 */
export async function deleteSessionSeries(
  sessionId: string,
  scope: SeriesScope = "following"
): Promise<{ ok: true; deleted: number; kept_past: number }> {
  const { data } = await api.delete<{ ok: true; deleted: number; kept_past: number }>(
    `/sessions/${sessionId}/series`,
    { params: { scope } }
  );
  return data;
}

// Club coaches and athletes
export interface ClubCoach {
  id: string;
  certifications: string | null;
  // The backend returns the timestamp under `joined_at` (the date the
  // coach_profiles row was created). Earlier callers used `created_at`,
  // which was always undefined and rendered as "Invalid date".
  joined_at: string;
  user: {
    id: string;
    email: string;
    full_name: string | null;
    // "active" | "demo" | "inactive" — surfaced so the list page can
    // render a status badge without an extra request per row.
    status?: "active" | "demo" | "inactive";
    /// Whether they have ever proved they can read that mailbox. An account
    /// created for them and never claimed is not "active" in any sense a club
    /// admin would recognise.
    email_verified?: boolean;
  };
  /// Distinct athletes across every team this coach leads or assists.
  athletes_count?: number;
  /// The sports this coach is assigned to at this club.
  sports?: { id: string; name: string }[];
  teams: {
    id: string;
    name: string;
    // Head coach via teams.coach_id vs a team-scoped coach role (secondary).
    is_head?: boolean;
    age_group?: string | null;
    sport_type?: string | null;
  }[];
}

export interface ClubAthlete {
  id: string;
  user: {
    id: string;
    email: string;
    full_name: string | null;
  };
  team: { id: string; name: string } | null;
  joined_at: string | null;
  stats: {
    attendance: {
      present: number;
      late: number;
      absent: number;
      excused: number;
      total: number;
      rate: number;
    };
    points: number;
  };
}

/**
 * Give somebody already at the club a second staff role, with no invitation.
 *
 * For a coach who is already here and confirmed and is now also made
 * coordinator of their sport: an invitation email to join a club they already
 * work for is a round trip that teaches them to ignore our emails.
 */
export async function grantClubRole(
  clubId: string,
  payload: { user_id: string; role_code: "coach" | "coordinator"; sport_id?: string }
): Promise<{ ok: boolean; added: boolean; message: string }> {
  const { data } = await api.post<{ ok: boolean; added: boolean; message: string }>(
    `/clubs/${clubId}/grant-role`,
    payload
  );
  return data;
}

export async function getClubCoaches(clubId: string): Promise<ClubCoach[]> {
  const { data } = await api.get<ClubCoach[]>(`/clubs/${clubId}/coaches`);
  return data;
}

export async function getClubAthletes(clubId: string): Promise<ClubAthlete[]> {
  const { data } = await api.get<ClubAthlete[]>(`/clubs/${clubId}/athletes`);
  return data;
}

// Club sessions
export interface ClubSession {
  id: string;
  team_id: string;
  coach_id?: string | null;
  title: string;
  type: "sport" | "educational";
  start_at: string;
  end_at: string;
  location_text: string | null;
  location_id?: string | null;
  notes?: string | null;
  /// Set on the FIRST occurrence of a series; the siblings point at it.
  is_recurring?: boolean;
  recurring_parent_id?: string | null;
  team: { id: string; name: string; sport_type?: string | null; age_group?: string | null };
  location?: { id: string; name: string } | null;
  coach?: { id: string; full_name: string | null; email: string } | null;
  _count?: { attendance: number };
  present_count?: number;
}

// Sessions for the signed-in user, auto-scoped by the backend to the teams they
// belong to (all of them, for a multi-team athlete) or coach. No club_id filter
// so it spans every team the athlete is on, even across clubs.
export async function getMySessions(): Promise<ClubSession[]> {
  const { data } = await api.get<{ data: ClubSession[] }>(`/sessions?pageSize=2000`);
  return data.data;
}

export async function getClubSessions(clubId: string): Promise<ClubSession[]> {
  // pageSize is set high so the admin sessions list returns the full set in
  // one shot. Backend caps the value at 2000 — well above what any single
  // club accumulates in years of weekly training. If we ever start seeing
  // clubs exceed that, switch this to a paginated loop.
  const { data } = await api.get<{ total: number; page: number; pageSize: number; data: ClubSession[] }>(`/sessions?club_id=${clubId}&pageSize=2000`);
  return data.data;
}

// Club invoices
export interface ClubInvoice {
  id: string;
  club_id: string;
  invoice_number: string | null;
  // Assigned at creation; the fiscal number arrives only on payment.
  proforma_number?: string | null;
  issued_at?: string | null;
  stripe_invoice_id: string;
  stripe_payment_link: string | null;
  amount_cents: number;
  amount_ron_cents: number | null;
  currency: string;
  exchange_rate: number | null;
  status: "draft" | "open" | "paid" | "void" | "uncollectible" | "cancelled";
  period_start: string | null;
  period_end: string | null;
  athlete_count: number | null;
  paid_at: string | null;
  pdf_url: string | null;
  hosted_invoice_url: string | null;
  created_at: string;
  club?: {
    id: string;
    name: string;
  };
}

export async function getClubInvoices(clubId: string): Promise<ClubInvoice[]> {
  const { data } = await api.get<ClubInvoice[]>(`/invoices?clubId=${clubId}`);
  return data;
}

// Club details
export interface ClubDetails {
  id: string;
  name: string;
  logo_url: string | null;
  description: string | null;
  // Business information
  legal_name: string | null;
  cui: string | null;
  registration_number: string | null;
  business_address: string | null;
  bank_account: string | null;
  bank_name: string | null;
  // Public information
  public_name: string | null;
  physical_address: string | null;
  city: string | null;
  county: string | null;
  country: string | null;
  website: string | null;
  brand_color?: string | null;
  brand_color_secondary?: string | null;
  // Configuration
  sports?: string[];
  age_groups?: string[];
  gallery?: string[];
  // System
  plan_code: string;
  status: string;
  created_at: string;
}

export async function getClubDetails(clubId: string): Promise<ClubDetails> {
  const { data } = await api.get<ClubDetails>(`/clubs/${clubId}`);
  return data;
}

export async function updateClub(clubId: string, payload: Partial<ClubDetails>): Promise<ClubDetails> {
  const { data } = await api.patch<ClubDetails>(`/clubs/${clubId}`, payload);
  return data;
}

// Experience level (derived from lifetime positive points).
export interface LevelInfo {
  xp: number;
  level: number;
  title: string;
  xp_into_level: number;
  xp_for_next: number;
  progress_pct: number;
}

// Points summary
export interface PointsSummary {
  total: number;
  this_week: number;
  this_month: number;
  breakdown: { source: string; total: number }[];
  recent_history: { id: string; amount: number; source: string; description: string | null; created_at: string }[];
  level?: LevelInfo;
}

export async function getPointsSummary(): Promise<PointsSummary> {
  const { data } = await api.get<PointsSummary>("/points/me/summary");
  return data;
}

// User profile update
export async function updateUserProfile(payload: { full_name?: string; locale?: string; profile_url?: string; phone?: string; date_of_birth?: string | null }): Promise<any> {
  const { data } = await api.patch("/users/me", payload);
  return data;
}

// Change user status (admin only)
export async function changeUserStatus(userId: string, status: "active" | "demo" | "inactive"): Promise<any> {
  const { data } = await api.patch(`/users/${userId}/status`, { status });
  return data;
}

// Change password
export async function changePassword(currentPassword: string, newPassword: string): Promise<any> {
  const { data } = await api.post("/users/me/change-password", { currentPassword, newPassword });
  return data;
}

// Update athlete profile (guardian info)
export async function updateAthleteProfile(payload: { guardian_contact?: { name?: string; email?: string; phone?: string; relationship?: string }; has_guardian?: boolean }): Promise<any> {
  const { data } = await api.patch("/users/me/athlete-profile", payload);
  return data;
}

// Athlete attendance history
export interface AttendanceRecord {
  id: string;
  session: {
    id: string;
    title: string;
    start_at: string;
    type: "sport" | "educational";
    team: { name: string };
  };
  status: "present" | "late" | "absent" | "excused";
  reason: string | null;
  created_at: string;
}

export async function getMyAttendanceHistory(): Promise<AttendanceRecord[]> {
  const { data } = await api.get<AttendanceRecord[]>("/sessions/attendance/my-history");
  return data;
}

// Club Locations
export type DaySchedule = {
  open: string;  // "08:00" format
  close: string; // "22:00" format
  closed?: boolean;
};

export type WeeklySchedule = {
  monday?: DaySchedule;
  tuesday?: DaySchedule;
  wednesday?: DaySchedule;
  thursday?: DaySchedule;
  friday?: DaySchedule;
  saturday?: DaySchedule;
  sunday?: DaySchedule;
};

export interface ClubLocation {
  id: string;
  club_id: string;
  name: string;
  address: string | null;
  description: string | null;
  capacity: number | null;
  is_active: boolean;
  schedule: WeeklySchedule | null;
  created_at: string;
  updated_at: string;
}

export async function getClubLocations(clubId: string): Promise<ClubLocation[]> {
  const { data } = await api.get<ClubLocation[]>(`/clubs/${clubId}/locations`);
  return data;
}

export async function createClubLocation(clubId: string, payload: {
  name: string;
  address?: string;
  description?: string;
  capacity?: number;
  schedule?: WeeklySchedule;
}): Promise<ClubLocation> {
  const { data } = await api.post<ClubLocation>(`/clubs/${clubId}/locations`, payload);
  return data;
}

export async function updateClubLocation(clubId: string, locationId: string, payload: {
  name?: string;
  address?: string;
  description?: string;
  capacity?: number;
  is_active?: boolean;
  schedule?: WeeklySchedule;
}): Promise<ClubLocation> {
  const { data } = await api.patch<ClubLocation>(`/clubs/${clubId}/locations/${locationId}`, payload);
  return data;
}

export async function deleteClubLocation(clubId: string, locationId: string): Promise<void> {
  await api.delete(`/clubs/${clubId}/locations/${locationId}`);
}

// Location Occupancy (Calendar view)
export interface LocationOccupancySession {
  id: string;
  title: string;
  start_at: string;
  end_at: string;
  type: "sport" | "educational";
  location_id: string;
  team: { id: string; name: string };
  coach: { id: string; full_name: string | null; email: string };
  location: { id: string; name: string };
}

export interface LocationOccupancyData {
  locations: ClubLocation[];
  sessions: LocationOccupancySession[];
  occupancyByLocation: Record<string, LocationOccupancySession[]>;
}

export async function getLocationOccupancy(clubId: string, from: string, to: string): Promise<LocationOccupancyData> {
  const { data } = await api.get<LocationOccupancyData>(`/sessions/location-occupancy?club_id=${clubId}&from=${from}&to=${to}`);
  return data;
}

// Team Details (with coach and athletes)
export interface TeamDetails {
  id: string;
  name: string;
  /// The owning club. Needed by the edit form to list the club's coaches and
  /// sports — the viewer's active club is empty for a platform admin.
  club_id?: string | null;
  age_group: string | null;
  age_group_id: string | null;
  age_group_ref: AgeGroupRef | null;
  // Numeric "youngest age accepted" — optional override / legacy fallback
  // used by the athlete-add flow when the structured age_group_ref isn't
  // set. When age_group_ref.max_age is present, the gate prefers that.
  min_age?: number | null;
  sport_type: string | null;
  /// The catalogue id behind sport_type, so a dropdown can preselect exactly.
  sport_id?: string | null;
  coach_id: string | null;
  coach: {
    id: string;
    email: string;
    full_name: string | null;
  } | null;
  // All coaches attached to this team: the head coach via teams.coach_id PLUS
  // anyone with a team-scoped coach user_role. Surfaced by GET /teams/:teamId
  // so the team detail page can render the whole coaching staff.
  coaches?: {
    id: string;
    email: string;
    full_name: string | null;
  }[];
  athletes: {
    id: string;
    user: {
      id: string;
      email: string;
      full_name: string | null;
    };
  }[];
  _count: {
    sessions: number;
  };
}

export async function getTeamDetails(teamId: string): Promise<TeamDetails> {
  const { data } = await api.get<TeamDetails>(`/teams/${teamId}`);
  return data;
}

// Upload functions
export async function uploadClubLogo(file: File): Promise<{ url: string; thumbnail: string }> {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post<{ url: string; thumbnail: string }>("/upload/club-logo", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

export async function uploadClubGalleryImage(file: File): Promise<{ url: string; thumbnail: string }> {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post<{ url: string; thumbnail: string; optimized: string }>("/upload/image?folder=clubs/gallery", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return { url: data.optimized || data.url, thumbnail: data.thumbnail };
}

// Generic image upload used by the rich-text editor (quiz content etc.).
export async function uploadContentImage(file: File, folder = "content"): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await api.post<{ url: string; optimized?: string }>(`/upload/image?folder=${encodeURIComponent(folder)}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.optimized || data.url;
}

// ========== Products / Services ==========

export type RecurrenceType = "hourly" | "weekly" | "monthly";
export type PaymentStatus = "pending" | "paid" | "overdue";
export type PaymentMethod = "cash" | "card" | "bank_transfer";

export interface Product {
  id: string;
  club_id: string;
  name: string;
  price_cents: number;
  currency: string;
  recurrence_type: RecurrenceType;
  age_groups: string[];
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AthleteProduct {
  id: string;
  athlete_id: string;
  product_id: string;
  due_day: number | null;
  next_due_date: string | null;
  payment_status: PaymentStatus;
  payment_method: PaymentMethod | null;
  bank_reference: string | null;
  paid_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  product: {
    id: string;
    name: string;
    description: string | null;
    price_cents: number;
    currency: string;
    recurrence_type: RecurrenceType;
  };
}

export interface AthleteWithPayments {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  athlete_profiles: {
    age_group: string | null;
    team: { id: string; name: string } | null;
  } | null;
  // Full set of teams the athlete belongs to (multi-team membership). The
  // primary team is also reflected in athlete_profiles.team.
  teams?: { id: string; name: string }[];
  athlete_products: AthleteProduct[];
  /**
   * Set only while the club has not yet accepted them. They already have a
   * profile — that is why they appear on this roster — but nothing about them
   * may be changed until someone says yes.
   */
  pending_membership?: {
    id: string;
    team_id: string | null;
    team_name: string | null;
    sport_name?: string | null;
    requested_at: string;
  } | null;
  /// Sports across their teams, the team they are only waiting to join included.
  sports?: { id: string | null; name: string }[];
  /// Parents, for ringing somebody about an unaccepted child.
  parents?: { id: string; full_name: string | null; email: string; phone: string | null; relationship?: string }[];
  /// How they got into the club: an invitation we sent, a shared link, or a
  /// person at the club adding them.
  join_source?: {
    kind: "invitation" | "join_link" | "added_by_club" | "unknown";
    by_name: string | null;
    label: string | null;
  } | null;
  payment_summary: {
    total_due_cents: number;
    next_due_date: string | null;
    has_pending: boolean;
    has_overdue: boolean;
    products_count: number;
  };
}

// Product CRUD
export async function getProducts(clubId: string): Promise<Product[]> {
  const { data } = await api.get<Product[]>(`/products?club_id=${clubId}`);
  return data;
}

export async function getProduct(productId: string): Promise<Product> {
  const { data } = await api.get<Product>(`/products/${productId}`);
  return data;
}

export async function createProduct(payload: {
  club_id: string;
  name: string;
  price_cents: number;
  recurrence_type: RecurrenceType;
  age_groups?: string[];
  description?: string;
}): Promise<Product> {
  const { data } = await api.post<Product>("/products", payload);
  return data;
}

export async function updateProduct(productId: string, payload: {
  name?: string;
  price_cents?: number;
  recurrence_type?: RecurrenceType;
  age_groups?: string[];
  description?: string;
  is_active?: boolean;
}): Promise<Product> {
  const { data } = await api.put<Product>(`/products/${productId}`, payload);
  return data;
}

export async function deleteProduct(productId: string): Promise<void> {
  await api.delete(`/products/${productId}`);
}

// Athlete Product Assignments
export async function getAthleteProducts(athleteId: string): Promise<AthleteProduct[]> {
  const { data } = await api.get<AthleteProduct[]>(`/products/athlete/${athleteId}`);
  return data;
}

export async function assignProductToAthlete(athleteId: string, payload: {
  product_id: string;
  due_day?: number;
  next_due_date?: string;
  notes?: string;
}): Promise<AthleteProduct> {
  const { data } = await api.post<AthleteProduct>(`/products/athlete/${athleteId}`, payload);
  return data;
}

export async function updateAthleteProduct(assignmentId: string, payload: {
  due_day?: number | null;
  next_due_date?: string | null;
  payment_status?: PaymentStatus;
  payment_method?: PaymentMethod;
  bank_reference?: string;
  notes?: string;
}): Promise<AthleteProduct> {
  const { data } = await api.put<AthleteProduct>(`/products/athlete-product/${assignmentId}`, payload);
  return data;
}

export async function removeAthleteProduct(assignmentId: string): Promise<void> {
  await api.delete(`/products/athlete-product/${assignmentId}`);
}

// Club Athletes with Payment Info
export async function getClubAthletesWithPayments(
  clubId: string,
  filters?: { search?: string; team_id?: string; age_group?: string; payment_status?: string }
): Promise<AthleteWithPayments[]> {
  const params = new URLSearchParams();
  if (filters?.search) params.set("search", filters.search);
  if (filters?.team_id) params.set("team_id", filters.team_id);
  if (filters?.age_group) params.set("age_group", filters.age_group);
  if (filters?.payment_status) params.set("payment_status", filters.payment_status);
  const url = `/products/club-athletes/${clubId}${params.toString() ? `?${params}` : ""}`;
  const { data } = await api.get<AthleteWithPayments[]>(url);
  return data;
}

// Payment Summary
export interface PaymentSummary {
  month: string;
  collected_cents: number;
  collected_count: number;
  overdue_cents: number;
  overdue_count: number;
  pending_cents: number;
  pending_count: number;
  team_summaries: Array<{
    team_id: string;
    team_name: string;
    collected_cents: number;
    overdue_cents: number;
    pending_cents: number;
  }>;
}

export async function getPaymentSummary(clubId: string, teamId?: string): Promise<PaymentSummary> {
  const params = new URLSearchParams();
  if (teamId) params.set("team_id", teamId);
  const url = `/products/payment-summary/${clubId}${params.toString() ? `?${params}` : ""}`;
  const { data } = await api.get<PaymentSummary>(url);
  return data;
}

// ---- Receivables (what the club must collect from its athletes) ----

export type ReceivableRow = {
  id: string;
  athlete: { id: string; full_name: string | null; email: string; phone: string | null };
  teams: { id: string; name: string }[];
  coaches: { id: string; full_name: string | null }[];
  product: { id: string; name: string; price_cents: number; currency: string; recurrence_type: RecurrenceType };
  amount_cents: number;
  currency: string;
  due_day: number | null;
  next_due_date: string | null;
  payment_status: "pending" | "paid" | "overdue";
  is_overdue: boolean;
  /** What the club chose not to collect here — a bursary or waiver. */
  waived_cents: number;
  waived_periods: number;
  /** The oldest period still owed — what a payment plan would apply to. */
  current_charge_id: string | null;
  /** How many slices that period is split into. 0 = no plan. */
  instalment_count: number;
  payment_method: PaymentMethod | null;
  paid_at: string | null;
  /** Actually collected on this assignment within the selected period. */
  collected_cents: number;
  last_payment: {
    amount_cents: number;
    paid_at: string;
    payment_method: PaymentMethod;
    collected_by_coach: { id: string; full_name: string | null } | null;
    handed_over: boolean;
  } | null;
};

export type ReceivablesResponse = {
  rows: ReceivableRow[];
  totals: {
    count: number;
    outstanding_count: number;
    outstanding_cents: number;
    overdue_cents: number;
    collected_cents: number;
    /** Money waived. Its own figure, never part of what was collected. */
    waived_cents: number;
    cash_with_coaches_cents: number;
  };
};

export async function getReceivables(
  clubId: string,
  filters?: { from?: string; to?: string; team_id?: string; coach_id?: string; status?: string }
): Promise<ReceivablesResponse> {
  const params = new URLSearchParams();
  Object.entries(filters || {}).forEach(([k, v]) => {
    if (v) params.set(k, v);
  });
  const qs = params.toString();
  const { data } = await api.get<ReceivablesResponse>(`/products/receivables/${clubId}${qs ? `?${qs}` : ""}`);
  return data;
}

// Payment History
export interface PaymentHistoryItem {
  id: string;
  athlete_product_id: string;
  athlete_id: string;
  amount_cents: number;
  currency: string;
  payment_method: PaymentMethod;
  bank_reference: string | null;
  notes: string | null;
  paid_at: string;
  created_at: string;
  collected_by_coach_id: string | null;
  collected_by_coach: {
    id: string;
    full_name: string | null;
    email: string;
  } | null;
  athlete_product: {
    product: {
      id: string;
      name: string;
    };
  };
}

export async function getPaymentHistory(athleteId: string): Promise<PaymentHistoryItem[]> {
  const { data } = await api.get<PaymentHistoryItem[]>(`/products/payment-history/${athleteId}`);
  return data;
}

// My payment history (for athletes)
export async function getMyPaymentHistory(): Promise<PaymentHistoryItem[]> {
  const { data } = await api.get<PaymentHistoryItem[]>("/products/my-payment-history");
  return data;
}

// Child payment history (for parents)
export async function getChildPaymentHistory(childId: string): Promise<PaymentHistoryItem[]> {
  const { data } = await api.get<PaymentHistoryItem[]>(`/products/child/${childId}/payment-history`);
  return data;
}

// ---- Coach Payment Management ----

export interface CoachAthletesResponse {
  athletes: AthleteWithPayments[];
  teams: Array<{ id: string; name: string }>;
}

export async function getCoachAthletes(teamId?: string): Promise<CoachAthletesResponse> {
  const params = new URLSearchParams();
  if (teamId) params.set("team_id", teamId);
  const url = `/products/coach-athletes${params.toString() ? `?${params}` : ""}`;
  const { data } = await api.get<CoachAthletesResponse>(url);
  return data;
}

export async function coachMarkPayment(
  athleteProductId: string,
  payload: { payment_method: PaymentMethod; bank_reference?: string; notes?: string }
) {
  const { data } = await api.put(`/products/coach-mark-payment/${athleteProductId}`, payload);
  return data;
}

export interface CoachCashSummary {
  pending_cash_cents: number;
  pending_payments: Array<{
    id: string;
    amount_cents: number;
    currency: string;
    athlete_name: string;
    product_name: string;
    paid_at: string;
  }>;
  recent_handovers: Array<{
    id: string;
    amount_cents: number;
    currency: string;
    handed_over_at: string;
    notes: string | null;
  }>;
}

export async function getCoachCashSummary(): Promise<CoachCashSummary> {
  const { data } = await api.get<CoachCashSummary>("/products/coach-cash-summary");
  return data;
}

// ---- Admin Coach Collections ----

export interface CoachCollection {
  coach_id: string;
  coach_name: string;
  coach_email: string;
  coach_phone: string | null;
  pending_cash_cents: number;
  pending_payments_count: number;
  pending_payments: Array<{
    id: string;
    amount_cents: number;
    currency: string;
    athlete_name: string;
    product_name: string;
    paid_at: string;
  }>;
  total_handed_over_cents: number;
}

export interface CoachCollectionsResponse {
  coaches: CoachCollection[];
  all_coaches: CoachCollection[];
}

export async function getCoachCollections(clubId: string): Promise<CoachCollectionsResponse> {
  const { data } = await api.get<CoachCollectionsResponse>(`/products/admin/coach-collections/${clubId}`);
  return data;
}

export async function recordCoachHandover(payload: {
  coach_id: string;
  amount_cents: number;
  payment_ids: string[];
  notes?: string;
}) {
  const { data } = await api.post("/products/admin/coach-handover", payload);
  return data;
}

export interface HandoverRecord {
  id: string;
  coach_id: string;
  club_id: string;
  admin_id: string;
  amount_cents: number;
  currency: string;
  payment_ids: string[];
  notes: string | null;
  handed_over_at: string;
  coach_name: string;
  admin_name: string;
}

export async function getHandoverHistory(clubId: string, coachId?: string): Promise<HandoverRecord[]> {
  const params = new URLSearchParams();
  if (coachId) params.set("coach_id", coachId);
  const url = `/products/admin/handover-history/${clubId}${params.toString() ? `?${params}` : ""}`;
  const { data } = await api.get<HandoverRecord[]>(url);
  return data;
}

// ---- Athlete Physical Metrics ----

export interface AthleteMetrics {
  id: string;
  athlete_id: string;
  coach_id: string;
  club_id: string;
  height_cm: number | null;
  weight_kg: number | null;
  wingspan_cm: number | null;
  reach_cm: number | null;
  notes: string | null;
  measured_at: string;
  created_at: string;
  coach_name?: string;
}

export async function getAthleteMetrics(athleteId: string): Promise<AthleteMetrics[]> {
  const { data } = await api.get<AthleteMetrics[]>(`/metrics/athlete/${athleteId}`);
  return data;
}

export async function getAthleteLatestMetrics(athleteId: string): Promise<AthleteMetrics | null> {
  const { data } = await api.get<AthleteMetrics | null>(`/metrics/athlete/${athleteId}/latest`);
  return data;
}

export async function addAthleteMetrics(
  athleteId: string,
  payload: {
    height_cm?: number;
    weight_kg?: number;
    wingspan_cm?: number;
    reach_cm?: number;
    notes?: string;
    measured_at?: string;
  }
): Promise<AthleteMetrics> {
  const { data } = await api.post<AthleteMetrics>(`/metrics/athlete/${athleteId}`, payload);
  return data;
}

export async function updateAthleteMetrics(
  metricsId: string,
  payload: Partial<{
    height_cm: number | null;
    weight_kg: number | null;
    wingspan_cm: number | null;
    reach_cm: number | null;
    notes: string;
    measured_at: string;
  }>
): Promise<AthleteMetrics> {
  const { data } = await api.put<AthleteMetrics>(`/metrics/${metricsId}`, payload);
  return data;
}

export async function deleteAthleteMetrics(metricsId: string): Promise<void> {
  await api.delete(`/metrics/${metricsId}`);
}

export interface MetricsChartData {
  labels: string[];
  datasets: {
    height: (number | null)[];
    weight: (number | null)[];
    wingspan: (number | null)[];
    reach: (number | null)[];
  };
  raw: AthleteMetrics[];
}

export async function getAthleteMetricsChartData(athleteId: string): Promise<MetricsChartData> {
  const { data } = await api.get<MetricsChartData>(`/metrics/athlete/${athleteId}/chart-data`);
  return data;
}

export interface CoachAthletesWithMetrics {
  athletes: Array<{
    id: string;
    full_name: string;
    date_of_birth: string | null;
    athlete_profiles: {
      age_group: string | null;
      team: { id: string; name: string } | null;
    } | null;
    latest_metrics: AthleteMetrics | null;
    metrics_outdated: boolean;
  }>;
  teams: Array<{ id: string; name: string }>;
}

export async function getCoachAthletesWithMetrics(teamId?: string): Promise<CoachAthletesWithMetrics> {
  const params = new URLSearchParams();
  if (teamId) params.set("team_id", teamId);
  const url = `/metrics/coach/athletes${params.toString() ? `?${params}` : ""}`;
  const { data } = await api.get<CoachAthletesWithMetrics>(url);
  return data;
}

export interface MetricsReminder {
  reminder_due: boolean;
  next_reminder: string;
  last_reminded: string;
  athletes_needing_update: number;
  total_athletes: number;
}

export async function getCoachMetricsReminder(): Promise<MetricsReminder> {
  const { data } = await api.get<MetricsReminder>("/metrics/coach/reminders");
  return data;
}

export async function dismissMetricsReminder(): Promise<{ message: string; next_reminder: string }> {
  const { data } = await api.post<{ message: string; next_reminder: string }>("/metrics/coach/dismiss-reminder");
  return data;
}

// ---- Parent Management ----

export interface ParentChild {
  id: string;
  athlete_id: string;
  relationship: string;
  athlete: {
    id: string;
    full_name: string;
    email: string;
    profile_url: string | null;
    date_of_birth: string | null;
    athlete_profiles: {
      age_group: string | null;
      club_id: string;
      team_id: string | null;
      team: { id: string; name: string } | null;
      club: { id: string; name: string } | null;
    } | null;
  };
  // Latest plan subscription for the child (null if none). status "pending"
  // means a paid signup was started but never completed → offer to resume.
  // `cancel_at` is set when the parent has stopped the plan: it keeps working
  // until that moment, then the child drops to Free.
  subscription?: {
    plan_code: string;
    billing_period: string;
    status: string;
    cancel_at?: string | null;
    current_period_end?: string | null;
  } | null;
}

export async function getParentChildren(): Promise<ParentChild[]> {
  const { data } = await api.get<ParentChild[]>("/auth/parent-children");
  return data;
}

// Coach awards points to one of their athletes (backend enforces the athlete is
// on one of the coach's teams).
export async function coachAwardPoints(athleteId: string, points: number, reason: string): Promise<{ total_points: number }> {
  const { data } = await api.post<{ ok: boolean; total_points: number }>("/coach/points/award", { athlete_id: athleteId, points, reason });
  return data;
}

// Parent updates one of their children's account settings.
export interface UpdateChildPayload {
  full_name?: string;
  date_of_birth?: string | null;
  username?: string;
  email?: string;
  password?: string;
  profile_url?: string | null;
}
export async function updateParentChild(childId: string, payload: UpdateChildPayload): Promise<void> {
  await api.patch(`/parent/children/${childId}`, payload);
}

// Resume payment for a child's pending plan subscription. Returns a Stripe
// checkout URL to redirect to.
/**
 * Move a child from Free (or an abandoned paid signup) onto a paid plan.
 * Returns the Stripe Checkout URL to hand off to; the plan only becomes active
 * once the webhook confirms the payment.
 */
export async function upgradeChildPlan(
  childId: string,
  plan: string,
  billingPeriod: "month" | "year" = "month"
): Promise<string | null> {
  const { data } = await api.post<{ checkout_url: string | null }>(
    `/parent/children/${childId}/upgrade`,
    { plan, billing_period: billingPeriod }
  );
  return data?.checkout_url ?? null;
}

export type PlanChangePreview = {
  amount_due_cents: number;
  credit_cents: number;
  currency: string;
  proration_date: number;
  current_plan_code: string;
  current_billing_period: string;
  new_plan_code: string;
  new_plan_name: string;
  new_billing_period: "month" | "year";
  next_renewal_at: string | null;
};

/** What switching an ACTIVE paid plan would cost right now, prorated. */
export async function previewChildPlanChange(
  childId: string,
  plan: string,
  billingPeriod: "month" | "year" = "month"
): Promise<PlanChangePreview> {
  const { data } = await api.get<PlanChangePreview>(
    `/parent/children/${childId}/plan-preview`,
    { params: { plan, billing_period: billingPeriod } }
  );
  return data;
}

/**
 * Switch an ACTIVE paid plan to another one. Pass the `proration_date` from the
 * preview the parent approved so the charge matches what they were shown.
 */
export async function changeChildPlan(
  childId: string,
  plan: string,
  billingPeriod: "month" | "year",
  prorationDate?: number
): Promise<{ plan_code: string; payment_url?: string | null; invoice_status?: string | null }> {
  const { data } = await api.post(`/parent/children/${childId}/change-plan`, {
    plan,
    billing_period: billingPeriod,
    proration_date: prorationDate,
  });
  return data;
}

/**
 * Stop paying for a child's plan. Paid features stay on until the end of the
 * period already paid for; then the child becomes Free.
 */
export async function cancelChildPlan(childId: string): Promise<{ cancel_at: string | null }> {
  const { data } = await api.post<{ cancel_at: string | null }>(
    `/parent/children/${childId}/cancel-plan`,
    {}
  );
  return data;
}

/** Undo a scheduled cancellation while the paid period is still running. */
export async function resumeChildPlan(childId: string): Promise<void> {
  await api.post(`/parent/children/${childId}/resume-plan`, {});
}

export async function resumeChildPayment(childId: string): Promise<string | null> {
  const { data } = await api.post<{ checkout_url: string | null }>(`/parent/children/${childId}/pay`, {});
  return data.checkout_url;
}

// Parent registers another child from the app. Returns a Stripe checkout URL
// for paid plans (null for free).
export interface AddChildResult {
  child: { id: string; username: string | null; email: string | null };
  plan: { code: string; billing_period: string; requires_payment: boolean };
  checkout_url: string | null;
  checkout_error?: string;
}
export async function addParentChild(payload: {
  first_name: string;
  age: number;
  username?: string;
  email?: string;
  password: string;
  plan: string;
  billing_period?: "month" | "year";
  /**
   * A club's parent invitation. When present it decides the child's club and
   * team, and the plan is the club's free one — no plan is chosen and no
   * payment is taken.
   */
  invitation_token?: string;
}): Promise<AddChildResult> {
  const { data } = await api.post<AddChildResult>("/parent/children", payload);
  return data;
}

export interface ParentPendingInvitation {
  token: string;
  athlete_name: string | null;
  club: { id: string; name: string; public_name: string | null } | null;
  team: { id: string; name: string; age_group: string | null } | null;
  expires_at: string;
}

/** Club invitations awaiting a child account from this parent. */
export async function getParentPendingInvitations(): Promise<ParentPendingInvitation[]> {
  const { data } = await api.get<{ invitations: ParentPendingInvitation[] }>(
    "/parent/pending-invitations"
  );
  return data.invitations || [];
}

export interface ParentDiscountStatus {
  paid_count: number;
  discount_percent: number;
}
export async function getParentDiscountStatus(): Promise<ParentDiscountStatus> {
  const { data } = await api.get<ParentDiscountStatus>("/parent/discount-status");
  return data;
}

export interface PublicPlan {
  code: string;
  name: string;
  description: string | null;
  currency: string;
  // Display bullet list of what the plan includes.
  features: string[];
  is_free: boolean;
  price_cents_month: number;
  price_cents_year: number | null;
}
export async function getPublicPlans(): Promise<PublicPlan[]> {
  const { data } = await api.get<{ plans: PublicPlan[] }>("/public/plans");
  return data.plans || [];
}

// Parent API - get child's attendance history
export interface ChildAttendanceHistory {
  attendance: AttendanceRecord[];
  stats: {
    present: number;
    late: number;
    absent: number;
    excused: number;
    total: number;
    attendance_rate: number;
  };
}

export async function getChildAttendanceHistory(childId: string): Promise<ChildAttendanceHistory> {
  const { data } = await api.get<ChildAttendanceHistory>(`/sessions/athlete/${childId}/history`);
  return data;
}

// Parent API - get child's points summary
export async function getChildPointsSummary(childId: string): Promise<PointsSummary> {
  const { data } = await api.get<PointsSummary>(`/points/user/${childId}/summary`);
  return data;
}

// Parent API - get child's upcoming sessions
export async function getChildSessions(teamId: string): Promise<any[]> {
  const { data } = await api.get(`/sessions/team/${teamId}`);
  return data.filter((s: any) => new Date(s.start_at) > new Date());
}

// Parent API - get child's products (what to pay)
export async function getChildProducts(childId: string): Promise<{ products: AthleteProduct[]; club: { name: string; payment_methods: string[] | null } | null }> {
  const { data } = await api.get<{ products: AthleteProduct[]; club: { name: string; payment_methods: string[] | null } | null }>(`/products/child/${childId}`);
  return data;
}

// ======================= EVENTS API =======================

export type EventStatus = "draft" | "published" | "cancelled" | "completed";
export type EventInvitationStatus = "invited" | "confirmed" | "declined";

export interface Event {
  id: string;
  club_id: string;
  title: string;
  description: string | null;
  sport_id: string | null;
  photo_url: string | null;
  location_address: string | null;
  location_city: string | null;
  location_county: string | null;
  start_date: string;
  end_date: string;
  event_time: string | null;
  specifications: string | null;
  created_by: string;
  status: EventStatus;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  sport?: { id: string; name: string } | null;
  club?: { id: string; name: string };
  creator?: { id: string; full_name: string | null };
  invitations?: EventInvitation[];
  notes?: EventNote[];
  _count?: { invitations: number; notes: number };
}

export interface EventInvitation {
  id: string;
  event_id: string;
  athlete_id: string;
  team_id: string;
  invited_by: string;
  status: EventInvitationStatus;
  confirmed_by: string | null;
  confirmed_at: string | null;
  decline_reason: string | null;
  created_at: string;
  athlete?: { id: string; full_name: string | null; email?: string };
  team?: { id: string; name: string };
  coach?: { id: string; full_name: string | null };
  event?: Event;
}

export interface EventNote {
  id: string;
  event_id: string;
  author_id: string;
  author_role: "club_admin" | "coach";
  content: string;
  created_at: string;
  author?: { id: string; full_name: string | null };
}

export interface EventTeamWithAthletes {
  id: string;
  name: string;
  athletes: {
    athlete_id: string;
    athlete: { id: string; full_name: string | null; email?: string };
    alreadyInvited: boolean;
  }[];
  _count: { athletes: number };
}

// Club Admin - List events
export async function getEvents(clubId: string, status?: EventStatus): Promise<{ events: Event[]; total: number }> {
  const params = new URLSearchParams({ club_id: clubId });
  if (status) params.append("status", status);
  const { data } = await api.get<{ events: Event[]; total: number }>(`/events?${params.toString()}`);
  return data;
}

// Club Admin - Create event
export async function createEvent(payload: {
  club_id: string;
  title: string;
  description?: string;
  sport_id?: string;
  location_address?: string;
  location_city?: string;
  location_county?: string;
  start_date: string;
  end_date: string;
  event_time?: string;
  specifications?: string;
  coach_ids?: string[];
  team_ids?: string[];
}): Promise<Event> {
  const { data } = await api.post<Event>("/events", payload);
  return data;
}

// Club Admin - Get single event
export async function getEvent(eventId: string): Promise<Event> {
  const { data } = await api.get<Event>(`/events/${eventId}`);
  return data;
}

// Club Admin - Update event
export async function updateEvent(eventId: string, payload: Partial<{
  title: string;
  description: string;
  sport_id: string;
  location_address: string;
  location_city: string;
  location_county: string;
  start_date: string;
  end_date: string;
  event_time: string;
  specifications: string;
  coach_ids: string[];
  team_ids: string[];
}>): Promise<Event> {
  const { data } = await api.patch<Event>(`/events/${eventId}`, payload);
  return data;
}

// Club Admin - Delete event (draft only)
export async function deleteEvent(eventId: string): Promise<void> {
  await api.delete(`/events/${eventId}`);
}

// Club Admin - Publish event
export async function publishEvent(eventId: string): Promise<Event> {
  const { data } = await api.post<Event>(`/events/${eventId}/publish`);
  return data;
}

// Club Admin - Cancel event
export async function cancelEvent(eventId: string): Promise<Event> {
  const { data } = await api.post<Event>(`/events/${eventId}/cancel`);
  return data;
}

// Club Admin - Upload event photo
export async function uploadEventPhoto(eventId: string, file: File): Promise<{ photo_url: string }> {
  const formData = new FormData();
  formData.append("photo", file);
  const { data } = await api.post<{ photo_url: string }>(`/events/${eventId}/photo`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

// Coach - Get events for club
export async function getCoachEvents(clubId: string): Promise<{ events: Event[] }> {
  const { data } = await api.get<{ events: Event[] }>(`/events/coach?club_id=${clubId}`);
  return data;
}

// Coach - Get teams for inviting
export async function getEventTeams(eventId: string): Promise<{ teams: EventTeamWithAthletes[] }> {
  const { data } = await api.get<{ teams: EventTeamWithAthletes[] }>(`/events/${eventId}/teams`);
  return data;
}

// Coach - Invite athletes
export async function inviteAthletes(eventId: string, teamId: string, athleteIds: string[]): Promise<{ invitations: EventInvitation[]; count: number }> {
  const { data } = await api.post<{ invitations: EventInvitation[]; count: number }>(`/events/${eventId}/invite`, {
    team_id: teamId,
    athlete_ids: athleteIds,
  });
  return data;
}

// Coach - Remove invitation
export async function removeInvitation(eventId: string, invitationId: string): Promise<void> {
  await api.delete(`/events/${eventId}/invitations/${invitationId}`);
}

// Athlete - Get my invitations
export async function getMyEventInvitations(status?: EventInvitationStatus): Promise<{ invitations: EventInvitation[] }> {
  const params = status ? `?status=${status}` : "";
  const { data } = await api.get<{ invitations: EventInvitation[] }>(`/events/my-invitations${params}`);
  return data;
}

// Parent - Get child's invitations
export async function getChildEventInvitations(childId: string, status?: EventInvitationStatus): Promise<{ invitations: EventInvitation[] }> {
  const params = status ? `?status=${status}` : "";
  const { data } = await api.get<{ invitations: EventInvitation[] }>(`/events/child/${childId}/invitations${params}`);
  return data;
}

// Athlete/Parent - Confirm invitation
export async function confirmEventInvitation(invitationId: string): Promise<EventInvitation> {
  const { data } = await api.post<EventInvitation>(`/events/invitations/${invitationId}/confirm`);
  return data;
}

// Athlete/Parent - Decline invitation
export async function declineEventInvitation(invitationId: string, reason?: string): Promise<EventInvitation> {
  const { data } = await api.post<EventInvitation>(`/events/invitations/${invitationId}/decline`, { reason });
  return data;
}

// Notes - Get event notes
export async function getEventNotes(eventId: string): Promise<{ notes: EventNote[] }> {
  const { data } = await api.get<{ notes: EventNote[] }>(`/events/${eventId}/notes`);
  return data;
}

// Notes - Add note
export async function addEventNote(eventId: string, content: string): Promise<EventNote> {
  const { data } = await api.post<EventNote>(`/events/${eventId}/notes`, { content });
  return data;
}

export default api;
